import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSession } from "@/auth/SessionProvider";
import { apiGet } from "@/lib/apiClient";
import { cnDashboardModuleNavLink, CustomAppRailIcon } from "@/lib/dashboardModuleNav";
import { cn } from "@/lib/utils";

type AppRow = { id: string; label: string; slug: string };

type Props = {
  onNavigate?: () => void;
  collapsed?: boolean;
};

/** Lists org-owned custom apps under the Custom apps sidebar when that suite is active. */
export function CustomAppsCatalogSidebar({ onNavigate, collapsed = false }: Props) {
  const { pathname } = useLocation();
  const session = useSession();
  const [apps, setApps] = useState<AppRow[]>([]);

  const isCustomAppsArea =
    pathname === "/custom-apps" || pathname.startsWith("/custom-apps/");

  useEffect(() => {
    if (session.status !== "authenticated" || !isCustomAppsArea) {
      setApps([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const res = await apiGet<{ apps?: AppRow[] }>("/api/custom-apps");
      if (cancelled) return;
      if (res.ok && Array.isArray(res.data?.apps)) {
        setApps(res.data.apps);
      } else {
        setApps([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session.status, isCustomAppsArea, pathname]);

  if (!isCustomAppsArea || apps.length === 0) {
    return null;
  }

  return (
    <li>
      {collapsed ? null : (
        <p className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-600 dark:text-slate-400">
          Your org apps
        </p>
      )}
      <ul className="space-y-px">
        {apps.map((app) => {
          const href = `/custom-apps/${encodeURIComponent(app.slug)}`;
          const active = pathname === href || pathname.startsWith(`${href}/`);
          return (
            <li key={app.id}>
              <Link
                to={href}
                className={cn(
                  cnDashboardModuleNavLink(active),
                  collapsed && "justify-center gap-0 px-2",
                )}
                onClick={onNavigate}
                title={collapsed ? app.label : undefined}
              >
                <CustomAppRailIcon />
                {collapsed ? (
                  <span className="sr-only">{app.label}</span>
                ) : (
                  <span className="min-w-0 truncate">{app.label}</span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </li>
  );
}
